import { ContactRole } from "../store";

/**
 * Role-based navigation utility for the sequenced onboarding flow.
 *
 * This module provides utilities for determining navigation paths based on
 * the user's assigned roles. Each role grants access to specific pages:
 * - workday-admin: Workday configuration
 * - it-admin: Domain configuration
 * - sso-admin: SSO configuration
 */

// Define all onboarding pages and their required roles
interface PageConfig {
  path: string;
  requiredRoles: ContactRole[];
  label: string;
  stage: "workday" | "domain" | "sso" | "complete";
}

export const onboardingPages: PageConfig[] = [
  {
    path: "/onboarding/workday",
    requiredRoles: ["workday-admin"],
    label: "Workday Configuration",
    stage: "workday"
  },
  {
    path: "/onboarding/domain",
    requiredRoles: ["it-admin"],
    label: "Domain Configuration",
    stage: "domain"
  },
  {
    path: "/onboarding/sso",
    requiredRoles: ["sso-admin"],
    label: "SSO Configuration",
    stage: "sso"
  },
  {
    path: "/onboarding/completion",
    requiredRoles: ["workday-admin", "it-admin", "sso-admin"], // All roles see completion
    label: "Completion",
    stage: "complete"
  }
];

/**
 * Get pages that the user has access to based on their roles.
 *
 * IMPORTANT: This function requires valid roles to be passed.
 * If roles are empty, it returns only the completion page as a safe fallback.
 * Do NOT call this function until roles have been loaded from Firebase claims.
 */
export function getAccessiblePages(userRoles: ContactRole[]): PageConfig[] {
  // If no roles are set, return only completion page as a safe fallback
  // This prevents showing unauthorized pages when roles haven't loaded yet
  if (!userRoles || userRoles.length === 0) {
    return onboardingPages.filter(
      (page) => page.path === "/onboarding/completion"
    );
  }

  return onboardingPages.filter((page) =>
    page.requiredRoles.some((role) => userRoles.includes(role))
  );
}

/**
 * Check if user has access to a specific page.
 *
 * IMPORTANT: This function requires valid roles to be passed.
 * If roles are empty, it only allows access to the completion page.
 */
export function canAccessPage(
  userRoles: ContactRole[],
  pagePath: string
): boolean {
  // If no roles are set, only allow completion page as a safe fallback
  if (!userRoles || userRoles.length === 0) {
    return pagePath === "/onboarding/completion";
  }

  const page = onboardingPages.find((p) => p.path === pagePath);
  if (!page) {
    return false;
  }

  return page.requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Get the next page path based on current page and user roles
 */
export function getNextPage(
  currentPath: string,
  userRoles: ContactRole[]
): string | null {
  const accessiblePages = getAccessiblePages(userRoles);
  const currentIndex = accessiblePages.findIndex((p) => p.path === currentPath);

  if (currentIndex === -1 || currentIndex >= accessiblePages.length - 1) {
    return null;
  }

  return accessiblePages[currentIndex + 1].path;
}

/**
 * Get the previous page path based on current page and user roles
 */
export function getPreviousPage(
  currentPath: string,
  userRoles: ContactRole[]
): string | null {
  const accessiblePages = getAccessiblePages(userRoles);
  const currentIndex = accessiblePages.findIndex((p) => p.path === currentPath);

  if (currentIndex <= 0) {
    return null;
  }

  return accessiblePages[currentIndex - 1].path;
}

/**
 * Get the first page the user should see based on their roles
 * and the current completion state.
 *
 * When roles are empty (not yet loaded from Firebase claims), defaults to
 * the workday page to avoid prematurely routing to completion.
 */
export function getStartPage(
  userRoles: ContactRole[],
  stageCompletion: {
    workday: boolean;
    domain: boolean;
    sso: boolean;
  }
): string {
  // If roles haven't loaded yet, default to workday page rather than completion
  // This prevents a flash-redirect to completion when persisted store has stale rolesLoaded=true
  if (!userRoles || userRoles.length === 0) {
    return "/onboarding/workday";
  }

  const accessiblePages = getAccessiblePages(userRoles);

  // Find the first incomplete stage the user has access to
  for (const page of accessiblePages) {
    // Check if this stage is complete
    const isComplete =
      (page.stage === "workday" && stageCompletion.workday) ||
      (page.stage === "domain" && stageCompletion.domain) ||
      (page.stage === "sso" && stageCompletion.sso);

    if (!isComplete && page.stage !== "complete") {
      return page.path;
    }
  }

  // If all stages are complete, go to completion
  return "/onboarding/completion";
}

/**
 * Determine if user can proceed to next stage based on current completion
 */
export function canProceed(
  currentStage: "workday" | "domain" | "sso",
  userRoles: ContactRole[],
  stageCompletion: {
    workday: boolean;
    domain: boolean;
    sso: boolean;
  }
): boolean {
  // workday-admin can only proceed after workday is complete
  // it-admin can only proceed after domain is complete (and workday if they have that role)
  // sso-admin can only proceed after sso is complete

  if (currentStage === "workday") {
    return stageCompletion.workday;
  }

  if (currentStage === "domain") {
    // If user is also workday-admin, workday must be complete
    if (userRoles.includes("workday-admin") && !stageCompletion.workday) {
      return false;
    }
    return stageCompletion.domain;
  }

  if (currentStage === "sso") {
    // If user is also workday-admin, workday must be complete
    if (userRoles.includes("workday-admin") && !stageCompletion.workday) {
      return false;
    }
    // If user is also it-admin, domain must be complete
    if (userRoles.includes("it-admin") && !stageCompletion.domain) {
      return false;
    }
    return stageCompletion.sso;
  }

  return true;
}

/**
 * Get a human-readable description of what stages are waiting for the user
 */
export function getWaitingMessage(
  userRoles: ContactRole[],
  stageCompletion: {
    workday: boolean;
    domain: boolean;
    sso: boolean;
  }
): string | null {
  const waitingFor: string[] = [];

  // it-admin and sso-admin need to wait for workday to be completed
  if (
    (userRoles.includes("it-admin") || userRoles.includes("sso-admin")) &&
    !userRoles.includes("workday-admin") &&
    !stageCompletion.workday
  ) {
    waitingFor.push(
      "Workday configuration to be completed by the Workday Admin"
    );
  }

  if (waitingFor.length === 0) {
    return null;
  }

  return `Waiting for: ${waitingFor.join(", ")}`;
}
